/**
 * Phase 5 coverage primitives — small visual elements re-used across the
 * Media Coverage section, source drill-down, quadrant, and empty state.
 *
 * Each primitive enforces the CD-spec honesty constraints inline:
 *   - CovBars: level conveyed by filled-bars (0-3), not colour. CVD-safe.
 *   - AsymFlag: descriptive icon + word + mandatory hover "why". Never red.
 *   - CredBadge / Sentiment: icon + word always; colour secondary cue.
 */

import {
  COVERAGE_LEVEL_META, CREDIBILITY_META, SENTIMENT_META,
  type CoverageLevel, type CredibilityTier, type Sentiment as SentimentType,
  type Asymmetry, type CoverageLevel as Lvl,
} from './types';

/** 0-3 filled bars of 3 total, increasing-height stacking. The non-colour
 *  channel that carries the coverage level — works in greyscale, CVD-safe. */
export function CovBars({ level, size = 1 }: { level: CoverageLevel; size?: number }) {
  const heights = [7, 11, 15].map(h => h * size);
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'flex-end',
        gap: 2 * size, height: 15 * size,
      }}
      aria-label={`coverage ${COVERAGE_LEVEL_META[level].word}`}
    >
      {heights.map((h, i) => (
        <span
          key={i}
          style={{
            width: 4 * size,
            height: h,
            borderRadius: 1,
            background: i < level ? 'var(--bm-cov)' : 'var(--bm-cov-dim)',
          }}
        />
      ))}
    </span>
  );
}

/** Coverage badge — bars + "<word> coverage" + count line. Clickable → opens
 *  the source drill-down side-pane (caller passes `onClick`). */
export function CovBadge({
  level, count, onClick,
}: { level: CoverageLevel; count: number; onClick?: () => void }) {
  const m = COVERAGE_LEVEL_META[level];
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '6px 10px', borderRadius: 8,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        cursor: interactive ? 'pointer' : 'default',
        color: 'inherit',
        font: 'inherit',
      }}
    >
      <CovBars level={level} />
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, alignItems: 'flex-start' }}>
        <span style={{
          fontWeight: 650, fontSize: 14,
          color: level === 0 ? 'var(--bm-text-subtle)' : 'var(--bm-cov)',
        }}>{m.word} coverage</span>
        <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
          {count} item{count !== 1 ? 's' : ''}{interactive ? ' · show sources →' : ''}
        </span>
      </span>
    </button>
  );
}

/** Asymmetry flag — descriptive, never normative. Icon + word + hover "why".
 *  Two kinds: UNDER-REPORTED (amber, "pay attention") and HIGH-PROFILE (teal,
 *  "lots of attention"). Both NEVER use red (red is owned by the threat
 *  channel; we don't want any visual confusion with severity). */
export function AsymFlag({
  asymmetry, observedLevel, expectedLevel, threatTierWord,
}: {
  asymmetry: Asymmetry;
  observedLevel: Lvl;
  expectedLevel: Lvl;
  threatTierWord: string;
}) {
  const under = asymmetry.kind === 'under';
  const why = under
    ? `${COVERAGE_LEVEL_META[observedLevel].word.toLowerCase()} coverage for a ${threatTierWord} threat — expected ${COVERAGE_LEVEL_META[expectedLevel].word.toLowerCase()}. Under-reported events can spread with low public awareness.`
    : `${COVERAGE_LEVEL_META[observedLevel].word.toLowerCase()} coverage exceeds what this threat level typically draws — may reflect a media cycle rather than escalation.`;
  return (
    <span
      title={why}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 9px', borderRadius: 999,
        fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700, letterSpacing: '.4px',
        color: under ? 'var(--bm-cov-under)' : 'var(--bm-cov-high)',
        background: under ? 'var(--bm-cov-under-bg)' : 'var(--bm-cov-high-bg)',
        border: `1px solid ${under ? 'var(--bm-cov-under)' : 'var(--bm-cov-high)'}55`,
      }}
    >
      <span aria-hidden style={{ fontSize: 12 }}>{under ? '◍' : '▲'}</span>
      {under ? 'UNDER-REPORTED' : 'HIGH-PROFILE'}
      {asymmetry.strength === 'strong' && (
        <span style={{ opacity: 0.7, fontWeight: 500 }}>· {under ? 'severe gap' : 'well above'}</span>
      )}
      <span aria-hidden style={{ opacity: 0.5, marginLeft: 2 }}>?</span>
    </span>
  );
}

/** Credibility tier badge — T1/T2/T3 + word. Always shows both so the visual
 *  ramp (brightness) is reinforced by text (accessible without colour). */
export function CredBadge({ cred }: { cred: CredibilityTier }) {
  const m = CREDIBILITY_META[cred];
  return (
    <span
      title={`Tier ${cred} · ${m.word}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: 'var(--bm-font-mono)', fontSize: 10,
        color: m.cssVar,
        border: `1px solid ${m.cssVar}55`,
        borderRadius: 4,
        padding: '1px 5px',
      }}
    >
      <span style={{ fontWeight: 700 }}>{m.short}</span>{m.word}
    </span>
  );
}

/** Sentiment indicator — icon + word. Always pair them; colour is a faint
 *  secondary cue only. Sentiment describes the article's TONE, NEVER the
 *  outbreak's severity (an alarmist aggregator and a measured WHO report can
 *  describe the same event; the operator sees both + decides). */
export function SentimentChip({ value }: { value: SentimentType }) {
  const m = SENTIMENT_META[value];
  return (
    <span
      title={`Tone: ${m.word}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11, color: m.cssVar,
      }}
    >
      <span aria-hidden style={{ fontFamily: 'var(--bm-font-mono)', fontWeight: 700 }}>{m.icon}</span>{m.word}
    </span>
  );
}
